
import React from 'react';
import {
  Smartphone,
  Globe,
  Cpu,
  Layers,
  Database,
  ShieldCheck,
  Rocket,
  MessageCircle,
  ArrowRight
} from 'lucide-react';

const CustomDevDetail: React.FC = () => {
  return (
    <div className="pt-24 pb-20 bg-white">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-8 mb-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 animate-in fade-in slide-in-from-left-8 duration-700">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold uppercase tracking-wider">
              <Cpu size={16} /> Engineering Excellence
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 leading-tight">
              Ubah Ide Anda Menjadi <br /> <span className="text-indigo-600">Produk Digital.</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Kami membangun aplikasi web dan mobile kustom yang dirancang untuk performa tinggi, skalabilitas, dan pengalaman pengguna yang luar biasa.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="px-8 py-4 bg-indigo-600 text-white rounded-xl font-bold shadow-xl shadow-indigo-200 hover:bg-indigo-700 transition-all">
                Mulai Proyek Sekarang
              </button>
              <button className="px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition-all">
                Lihat Portfolio
              </button>
            </div>
          </div>
          <div className="relative group">
            <div className="absolute inset-0 bg-indigo-200 rounded-[3rem] blur-3xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
            <div className="relative bg-slate-900 rounded-[3rem] p-4 shadow-2xl border border-slate-800 overflow-hidden">
               <div className="bg-slate-800 h-6 flex items-center gap-1.5 px-4 mb-2">
                  <div className="w-2 h-2 rounded-full bg-red-400"></div>
                  <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
               </div>
               <img src="https://picsum.photos/seed/code/800/600" alt="Code Mockup" className="rounded-2xl opacity-90" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-slate-50 py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <h2 className="text-indigo-600 font-bold uppercase tracking-wider text-sm">Spesialisasi Kami</h2>
            <h3 className="text-4xl font-bold text-slate-900">Solusi Teknologi End-to-End</h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <ExpertiseCard 
              icon={<Globe size={40} className="text-blue-500" />}
              title="Web Development"
              description="Kami membangun aplikasi web yang responsif dan skalabel menggunakan stack modern seperti React, Next.js, dan Node.js."
              items={["Sistem ERP & CRM", "Dashboard Monitoring", "Portal Berita & Konten", "Sistem SaaS"]}
            />
            <ExpertiseCard 
              icon={<Smartphone size={40} className="text-indigo-500" />}
              title="Mobile Development"
              description="Aplikasi iOS dan Android dengan performa native menggunakan React Native atau Flutter."
              items={["E-commerce Mobile", "Aplikasi On-Demand", "Sistem Pelacakan Lokasi", "Social Networking"]}
            />
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
           <div className="flex flex-col md:flex-row gap-20 items-center">
              <div className="flex-1 space-y-8">
                 <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Proses Pengembangan <br /> <span className="text-indigo-600">"The DigiMaju Way"</span></h2>
                 <p className="text-lg text-slate-600">Kami mengadopsi metodologi Agile untuk memastikan transparansi dan fleksibilitas selama pengerjaan proyek.</p>
                 <div className="space-y-4">
                    <ProcessItem number="01" title="Discovery & Analysis" desc="Memahami kebutuhan bisnis dan menyusun dokumen spesifikasi teknis." />
                    <ProcessItem number="02" title="UI/UX Design" desc="Membuat prototype interaktif dan desain visual yang modern." />
                    <ProcessItem number="03" title="Development" desc="Proses coding yang rapi dengan review kode secara berkala." />
                    <ProcessItem number="04" title="Testing & QA" desc="Uji coba ketat untuk memastikan aplikasi bebas bug dan aman." />
                    <ProcessItem number="05" title="Deployment & Support" desc="Peluncuran aplikasi ke server dan pemeliharaan berkelanjutan." />
                 </div>
              </div>
              <div className="flex-1 relative">
                 <div className="bg-indigo-600 rounded-[4rem] p-12 md:p-20 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-10 opacity-10">
                       <Rocket size={120} />
                    </div>
                    <div className="relative z-10 space-y-8">
                       <h4 className="text-2xl font-bold italic">"Kami tidak hanya menulis kode, kami membangun solusi bisnis yang berkelanjutan."</h4>
                       <div className="flex items-center gap-4">
                          <img src="https://picsum.photos/seed/cto/100/100" className="w-12 h-12 rounded-full border-2 border-white/20" alt="Lead" />
                          <div>
                             <p className="font-bold">Hafizh</p>
                             <p className="text-indigo-200 text-sm">Lead Engineer</p>
                          </div>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-slate-50 overflow-hidden">
         <div className="container mx-auto px-4 md:px-8">
            <h3 className="text-center font-bold text-slate-400 uppercase tracking-[0.2em] text-sm mb-12">Stack Teknologi Unggulan Kami</h3>
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 items-center opacity-60">
               <StackIcon name="React / Next.js" icon={<Layers />} />
               <StackIcon name="Flutter / RN" icon={<Smartphone />} />
               <StackIcon name="Node.js / Go" icon={<Database />} />
               <StackIcon name="AWS / GCP" icon={<Globe />} />
               <StackIcon name="Security First" icon={<ShieldCheck />} />
            </div>
         </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
         <div className="container mx-auto px-4 md:px-8">
            <div className="bg-slate-900 rounded-[3rem] p-12 md:p-16 text-center text-white relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/50 to-transparent"></div>
               <div className="relative z-10 space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">Punya Proyek Kustom yang Menantang?</h2>
                  <p className="text-slate-400 max-w-2xl mx-auto text-lg">Konsultasikan kebutuhan teknis Anda secara gratis. Kami siap memberikan estimasi biaya dan waktu pengerjaan.</p>
                  <div className="flex flex-col md:flex-row justify-center gap-4 pt-4">
                     <button className="px-10 py-4 bg-indigo-600 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all">
                        Hubungi Tim Teknis <ArrowRight size={20} />
                     </button>
                     <button className="px-10 py-4 bg-white/10 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-white/20 transition-all">
                        Jadwal Meeting Zoom <MessageCircle size={20} />
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </section>
    </div>
  );
};

const ExpertiseCard: React.FC<{ icon: React.ReactNode, title: string, description: string, items: string[] }> = ({ icon, title, description, items }) => (
   <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
      <div className="mb-8">{icon}</div>
      <h4 className="text-2xl font-bold text-slate-900 mb-4">{title}</h4>
      <p className="text-slate-600 mb-8 leading-relaxed">{description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
         {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-500">
               <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
               {item}
            </div>
         ))}
      </div>
   </div>
);

const ProcessItem: React.FC<{ number: string, title: string, desc: string }> = ({ number, title, desc }) => (
   <div className="flex gap-6 group">
      <div className="text-2xl font-black text-indigo-100 group-hover:text-indigo-600 transition-colors duration-300">{number}</div>
      <div>
         <h4 className="font-bold text-slate-900 mb-1">{title}</h4>
         <p className="text-sm text-slate-500">{desc}</p>
      </div>
   </div>
);

const StackIcon: React.FC<{ name: string, icon: React.ReactNode }> = ({ name, icon }) => (
   <div className="flex items-center gap-3 text-slate-600 font-bold hover:text-indigo-600 transition-colors cursor-default">
      {icon}
      <span>{name}</span>
   </div>
);

export default CustomDevDetail;
